import { useEffect, useState } from "react";
import { AlertCircle, FileText } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import type { Invoice } from "./types";
import { inr } from "./utils";

interface InvoiceNumberUpdateModalProps {
  open: boolean;
  invoice: Invoice | null;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onOpenChange: (open: boolean) => void;
  onSave: (invoiceNumber: string) => void;
  onInputChange?: () => void;
}

const InvoiceNumberUpdateModal = ({
  open,
  invoice,
  isSubmitting = false,
  errorMessage,
  onOpenChange,
  onSave,
  onInputChange,
}: InvoiceNumberUpdateModalProps) => {
  const [invoiceNumber, setInvoiceNumber] = useState("");

  useEffect(() => {
    if (open) {
      setInvoiceNumber("");
    }
  }, [open]);

  if (!invoice) {
    return null;
  }

  const trimmedNumber = invoiceNumber.trim();
  const canSave = trimmedNumber.length > 0 && !isSubmitting;

  const handleSave = () => {
    if (!canSave) {
      return;
    }

    onSave(trimmedNumber);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="bg-primary/10 flex h-16 w-16 items-center justify-center rounded-full">
            <FileText className="text-primary h-8 w-8" />
          </div>

          <div className="space-y-2">
            <DialogTitle className="text-lg font-semibold text-[#111827]">
              Enter Invoice Number
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
              This is a recurring invoice. Enter the invoice number before
              marking it as sent.
            </DialogDescription>
          </div>

          <div className="bg-background w-full rounded-xl border border-gray-200 p-4 text-left">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="space-y-0.5">
                <p className="text-muted-foreground text-xs">Client</p>
                <p className="font-semibold text-[#111827]">{invoice.client}</p>
              </div>
              <div className="space-y-0.5">
                <p className="text-muted-foreground text-xs">Total Amount</p>
                <p className="font-semibold text-[#111827]">
                  {inr(invoice.total)}
                </p>
              </div>
              <div className="col-span-2 space-y-0.5">
                <p className="text-muted-foreground text-xs">Billing Period</p>
                <p className="text-primary font-medium">{invoice.period}</p>
              </div>
            </div>
          </div>

          <div className="w-full space-y-2 text-left">
            <Label
              htmlFor="recurring-invoice-number"
              className="text-sm font-medium text-[#111827]"
            >
              Invoice Number
            </Label>
            <Input
              id="recurring-invoice-number"
              value={invoiceNumber}
              disabled={isSubmitting}
              placeholder="e.g. 01/26-27"
              autoFocus
              className={`h-10 rounded-xl bg-[#fafafa] ${
                errorMessage
                  ? "border-red-500 focus-visible:ring-red-500"
                  : "border-gray-200"
              }`}
              onChange={event => {
                setInvoiceNumber(event.target.value);
                onInputChange?.();
              }}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSave();
                }
              }}
            />
            {errorMessage ? (
              <p className="flex items-center gap-1.5 text-xs text-red-600">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {errorMessage}
              </p>
            ) : null}
          </div>
        </div>

        <DialogFooter className="flex gap-3 sm:justify-center">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
            className="rounded-xl border-gray-300 bg-white px-5 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            className="bg-primary hover:bg-primary/90 rounded-xl px-5 text-white"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceNumberUpdateModal;
